/**
 * Reconnection manager with exponential backoff and jitter
 */

import { ReconnectionConfig } from '../types/connectionTypes';

export const DEFAULT_RECONNECTION_CONFIG: ReconnectionConfig = {
  initialDelay: 1000,       // 1 second
  maxDelay: 30000,          // 30 seconds
  maxRetries: 10,
  backoffMultiplier: 2,
  jitterRange: 0.3          // ±30% randomization
};

export class ReconnectionManager {
  private config: ReconnectionConfig;
  private attemptCount: number = 0;
  private reconnectTimer: NodeJS.Timeout | null = null;

  constructor(config: Partial<ReconnectionConfig> = {}) {
    this.config = { ...DEFAULT_RECONNECTION_CONFIG, ...config };
  }

  /**
   * Calculate exponential backoff delay with jitter
   * Formula: min(initialDelay * (multiplier ^ attempt), maxDelay) * (1 + random(-jitter, +jitter))
   */
  calculateDelay(attempt: number): number {
    const { initialDelay, maxDelay, backoffMultiplier, jitterRange } = this.config;

    // Exponential backoff
    const exponentialDelay = initialDelay * Math.pow(backoffMultiplier, attempt - 1);

    // Cap at max delay
    const cappedDelay = Math.min(exponentialDelay, maxDelay);

    // Add jitter (randomization to prevent thundering herd)
    const jitter = 1 + (Math.random() * 2 - 1) * jitterRange; // Range: [1-jitterRange, 1+jitterRange]

    return Math.floor(cappedDelay * jitter);
  }

  /**
   * Start reconnection attempt
   * @returns delay in milliseconds before next attempt
   */
  startReconnection(): number | null {
    this.attemptCount++;

    if (this.attemptCount > this.config.maxRetries) {
      console.warn(`Max reconnection attempts (${this.config.maxRetries}) reached`);
      return null;
    }

    const delay = this.calculateDelay(this.attemptCount);
    console.log(`Reconnection attempt ${this.attemptCount}/${this.config.maxRetries} scheduled in ${delay}ms`);

    return delay;
  }

  /**
   * Schedule reconnection callback
   */
  scheduleReconnect(callback: () => void): void {
    const delay = this.startReconnection();

    if (delay === null) {
      // Max retries reached
      return;
    }

    // Clear any existing timer
    this.clearTimer();

    // Schedule reconnection
    this.reconnectTimer = setTimeout(() => {
      callback();
    }, delay);
  }

  /**
   * Reset attempt counter (call on successful reconnection)
   */
  reset(): void {
    this.attemptCount = 0;
    this.clearTimer();
  }

  /**
   * Clear reconnection timer
   */
  clearTimer(): void {
    if (this.reconnectTimer) {
      clearTimeout(this.reconnectTimer);
      this.reconnectTimer = null;
    }
  }

  /**
   * Get current attempt count
   */
  getAttemptCount(): number {
    return this.attemptCount;
  }

  /**
   * Get max retries
   */
  getMaxRetries(): number {
    return this.config.maxRetries;
  }

  /**
   * Check if max retries reached
   */
  isMaxRetriesReached(): boolean {
    return this.attemptCount >= this.config.maxRetries;
  }

  /**
   * Check if currently attempting reconnection
   */
  isReconnecting(): boolean {
    return this.reconnectTimer !== null;
  }
}
